import { ATSPrincipalAppCandidateModalTabsTestIds } from 'data-testids/ATS';
import { CandidateRowInfo } from 'features/Principal/types/modalTypes';
import { formatPhoneNumber, preferenceMapping } from 'utils/util';
import { languageList } from 'utils/enums';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CachedIcon from '@mui/icons-material/Cached';
import CallIcon from '@mui/icons-material/Call';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LanguageIcon from '@mui/icons-material/Language';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import {
  PrincipalAppCandidateProfile,
  PrincipalAppPreferences,
  PrincipalAppRow,
} from 'features/Principal/types/candidatesListTypes';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import SchoolIcon from '@mui/icons-material/School';
import ToysOutlinedIcon from '@mui/icons-material/ToysOutlined';

export const getLeftCandidateInfo = (row: PrincipalAppRow): CandidateRowInfo[] => {
  const secondaryPhoneText = row.candidate.profile?.phone_work
    ? formatPhoneNumber(row.candidate.profile.phone_work)
    : 'Not provided';

  const getLanguageText = (languages: Array<number | string> | null | undefined) => {
    const labelsByValue: Record<string, string> = {};

    for (const language of languageList()) {
      labelsByValue[String(language.value)] = language.label;
    }

    const labels: string[] = [];

    for (const lang of languages ?? []) {
      const label = labelsByValue[String(lang)];
      if (label) labels.push(label);
    }

    if (labels.length === 0) return 'None';

    return labels.join(', ');
  };

  return [
    {
      Icon: MailOutlineIcon,
      headerText: 'Email',
      content: row.candidate.email,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.BASIC_INFO_EMAIL,
    },
    {
      Icon: CallIcon,
      headerText: 'Secondary phone',
      content: secondaryPhoneText,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.BASIC_INFO_SECONDARY_PHONE,
    },
    {
      Icon: LanguageIcon,
      headerText: 'Languages',
      content: getLanguageText(row.candidate.profile.languages),
      testId: ATSPrincipalAppCandidateModalTabsTestIds.BASIC_INFO_LANGUAGES,
    },
    {
      Icon: BusinessCenterOutlinedIcon,
      headerText: 'Years of experience',
      content: row.candidate.profile.years_experience,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.BASIC_INFO_YEARS_EXPERIENCE,
    },
  ];
};

export const formatAddress = (profile: PrincipalAppCandidateProfile) => {
  if (!profile) return <></>;

  const statePart = profile.address_state ? ` ${profile.address_state}` : '';
  const streetPart = profile.address_apt
    ? `${profile.address_street} ${profile.address_apt}`
    : profile.address_street;

  if (!statePart && !streetPart && !profile.address_city && !profile.address_zip) {
    return 'Not provided';
  }

  return `${streetPart}, ${profile.address_city},${statePart} ${profile.address_zip}`;
};

export const getRightCandidateData = (row: PrincipalAppRow) => {
  return [
    {
      Icon: CallIcon,
      headerText: 'Phone',
      content: formatPhoneNumber(row.candidate.profile?.phone_cell),
      testId: ATSPrincipalAppCandidateModalTabsTestIds.BASIC_INFO_CONTACT,
    },
    {
      Icon: RoomOutlinedIcon,
      headerText: 'Address',
      content: formatAddress(row.candidate.profile),
      testId: ATSPrincipalAppCandidateModalTabsTestIds.BASIC_INFO_ADDRESS,
    },
    {
      Icon: PersonOutlineOutlinedIcon,
      headerText: 'Source',
      content: row.application?.source?.label,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.BASIC_INFO_REPORTED_SOURCE,
    },
  ];
};

export const getRolePreferences = (preferences: PrincipalAppPreferences) => {
  const mapped = preferenceMapping(preferences);

  const rolePreferenceConfig = [
    {
      key: 'employment_type',
      Icon: QueryBuilderIcon,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.PREFERENCES_EMPLOYMENT_TYPE,
    },
    {
      key: 'grades',
      Icon: SchoolIcon,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.PREFERENCES_GRADES,
    },
    {
      key: 'hours_per_week',
      Icon: HourglassEmptyIcon,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.PREFERENCES_HOURS_PER_WEEK,
    },
    {
      key: 'categories',
      Icon: BookmarkBorderIcon,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.PREFERENCES_CATEGORIES,
    },
  ] as const;

  return rolePreferenceConfig.map(({ key, Icon, testId }) => ({
    Icon,
    headerText: mapped[key].label,
    content: preferences[key] && mapped[key].value ? mapped[key].value : 'No response',
    testId,
  }));
};

export const getSchoolPreferences = (preferences: PrincipalAppPreferences) => {
  const mapped = preferenceMapping(preferences);

  const schoolPreferenceConfig = [
    {
      key: 'miles_within',
      Icon: ToysOutlinedIcon,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.PREFERENCES_MILES_WITHIN,
    },
    {
      key: 'title_1_school',
      Icon: MilitaryTechIcon,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.PREFERENCES_TITLE_1_SCHOOL,
    },
    {
      key: 'multilingual_school',
      Icon: LanguageIcon,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.PREFERENCES_MULTILINGUAL_SCHOOL,
    },
    {
      key: 'turnaround_school',
      Icon: CachedIcon,
      testId: ATSPrincipalAppCandidateModalTabsTestIds.PREFERENCES_TURNAROUND_SCHOOL,
    },
  ] as const;

  return schoolPreferenceConfig.map(({ key, Icon, testId }) => ({
    Icon,
    headerText: mapped[key].label,
    content: preferences[key] && mapped[key].value ? mapped[key].value : 'No response',
    testId,
  }));
};
